// Trimmed port of /Users/rs/dev/oq-images/src/lib/sceneAssets.ts.
// Drops: ~3000 lines of procedural geometry, all 6 style variants, hatching,
// corner beams, power cables, attachLogoIfNeeded (handled in assetRegistry).
// Keeps: theme palette, lighting setup, applyTheme.
import * as THREE from "three";

export type ThemeMode = "light" | "dark";

export type ThemePalette = {
  bg: number;
  fogNear: number;
  fogFar: number;
  ground: number;
  dot: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  keyColor: number;
  keyIntensity: number;
  fillIntensity: number;
  rimColor: number;
  rimIntensity: number;
  environmentIntensity: number;
  /** Subtle pool of light at the centre of the floor (point light, finite distance). */
  floorGlowColor: number;
  floorGlowIntensity: number;
};

export type SceneLights = {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
  floorGlow: THREE.PointLight;
};

export const THEMES: Record<ThemeMode, ThemePalette> = {
  light: {
    bg: 0xf7f5f2,
    fogNear: 40,
    fogFar: 110,
    ground: 0xf2efea,
    dot: 0xcfcac2,
    hemiSky: 0xffffff,
    hemiGround: 0xbfbab4,
    hemiIntensity: 1.0,
    keyColor: 0xffffff,
    keyIntensity: 1.25,
    fillIntensity: 0.35,
    rimColor: 0x7ea8d8,
    rimIntensity: 0.12,
    environmentIntensity: 0.55,
    floorGlowColor: 0xffffff,
    floorGlowIntensity: 0.0,
  },
  dark: {
    // V5 dark palette ported from /Users/rs/dev/oq-images. Background and
    // ground at 0x0a0a0b, hemi 0.34 with sky 0x18181e, fog far out so the
    // back of the scene fades softly. Material clearcoat + fresnel rim
    // (in assetRegistry) carries the milky form-shading.
    bg: 0x0d0e10,
    fogNear: 110,
    fogFar: 260,
    ground: 0x0d0e10,
    dot: 0xc8ccd4,
    hemiSky: 0x1f242c,
    hemiGround: 0x0a0a0c,
    hemiIntensity: 0.55,
    keyColor: 0xeaf3ff,
    keyIntensity: 1.85,
    fillIntensity: 0.18,
    rimColor: 0x4a6a8c,
    rimIntensity: 0.30,
    environmentIntensity: 0.22,
    floorGlowColor: 0x6a90c0,
    floorGlowIntensity: 0.55,
  },
};

export function setupSceneLights(scene: THREE.Scene): SceneLights {
  const hemi = new THREE.HemisphereLight(0xffffff, 0xbfbab4, 0.2);
  hemi.name = "hemi";
  scene.add(hemi);

  const key = new THREE.DirectionalLight(0xd8edff, 2.35);
  key.name = "key";
  // Steeper, slightly side-lit angle so the cap edges catch a clean rim
  // highlight (matches the reference hero shot).
  key.position.set(28, 42, 18);
  key.castShadow = true;
  key.shadow.camera.left = -70;
  key.shadow.camera.right = 70;
  key.shadow.camera.top = 70;
  key.shadow.camera.bottom = -70;
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 200;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.radius = 10;
  key.shadow.bias = -0.0003;
  key.shadow.normalBias = 0.02;
  scene.add(key);

  const fill = new THREE.DirectionalLight(0x8aa0bc, 0.035);
  fill.name = "fill";
  fill.position.set(-20, 20, -10);
  scene.add(fill);

  const rim = new THREE.DirectionalLight(0x1e72b8, 0.58);
  rim.name = "blue-rim";
  rim.position.set(-32, 20, -28);
  scene.add(rim);

  // Subtle floor glow: a low-intensity point light just above the ground,
  // cool blueish, with a finite distance so it falls off before reaching the
  // tops of objects. Lifts the centre of the floor and the bottom edges of
  // the objects so the scene reads "lit pad" instead of "void".
  const floorGlow = new THREE.PointLight(0x6a90c0, 0.0, 28, 1.6);
  floorGlow.name = "floor-glow";
  floorGlow.position.set(0, 1.4, 0);
  scene.add(floorGlow);

  return { hemi, key, fill, rim, floorGlow };
}

export function applyTheme(
  theme: ThemeMode,
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  ground: THREE.Mesh,
  dotMat: THREE.MeshBasicMaterial,
  hemi: THREE.HemisphereLight,
  key: THREE.DirectionalLight,
  fill: THREE.DirectionalLight,
  rim: THREE.DirectionalLight,
  floorGlow?: THREE.PointLight,
): void {
  const p = THEMES[theme];
  renderer.setClearColor(p.bg, 1);
  scene.background = new THREE.Color(p.bg);
  scene.fog = new THREE.Fog(p.bg, p.fogNear, p.fogFar);
  scene.environmentIntensity = p.environmentIntensity;

  const groundMat = ground.material as THREE.MeshStandardMaterial;
  groundMat.color.setHex(p.ground);
  groundMat.metalness = 0;
  groundMat.roughness = 1;
  groundMat.envMapIntensity = theme === "dark" ? 0.05 : 0.2;
  groundMat.needsUpdate = true;

  dotMat.color.setHex(p.dot);

  hemi.color.setHex(p.hemiSky);
  hemi.groundColor.setHex(p.hemiGround);
  hemi.intensity = p.hemiIntensity;
  key.color.setHex(p.keyColor);
  key.intensity = p.keyIntensity;
  fill.intensity = p.fillIntensity;
  rim.color.setHex(p.rimColor);
  rim.intensity = p.rimIntensity;
  if (floorGlow) {
    floorGlow.color.setHex(p.floorGlowColor);
    floorGlow.intensity = p.floorGlowIntensity;
  }
}
